import { useQueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import { formatPhoneNumber } from "react-phone-number-input"

import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import { Avatar, Button } from "@atoms/index"

import { ListItemStack } from "@molecules"
import GenericForm from "@molecules/Form/GenericForm/GenericForm"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import ListItemDivider from "@molecules/ListItem/ListItemDivider/ListItemDivider"
import ToggleListItem from "@molecules/ListItem/ToggleListItem/ToggleListItem"
import toast from "@molecules/Toast/Toast"

import EmailFlowDialog from "@pages/Settings/SettingsProfile/components/UserContactsForms/EmailFlowDialog/EmailFlowDialog"
import PhoneFlowDialog from "@pages/Settings/SettingsProfile/components/UserContactsForms/PhoneFlowDialog/PhoneFlowDialog"

import { NOTIFICATION_ENDPOINTS } from "@endpoints/notification"

import styles from "./NotificationMethodsSection.module.scss"
import { notificationMethodsQueryKey, useNotificationMethodsQuery } from "./NotificationMethodsSection.store"
import { NotificationMethodsList } from "./NotificationMethodsSection.types"

export default function NotificationMethodsSection() {
    const queryClient = useQueryClient()
    const { user } = useUser()

    const {
        data: defaultMethodsPreference,
        isLoading,
        isError: isErrorOnGettingDefaultData,
    } = useNotificationMethodsQuery()

    useEffect(() => {
        if (isErrorOnGettingDefaultData) {
            toast({
                type: "error",
                variant: "default",
                title: "Could not retrieve notification method preference",
            })
        }
    }, [isErrorOnGettingDefaultData])

    const hasVerifiedEmail = !!user?.email_verified
    const hasPhoneNumber = !!user?.phone
    const hasVerifiedPhone = hasPhoneNumber && !!user?.phone_verified

    return (
        <ListItemStack>
            <GenericForm<NotificationMethodsList, NotificationMethodsList, Error, NotificationMethodsList>
                formConfig={{
                    defaultValues: undefined,
                }}
                mutationConfig={{
                    method: "PATCH",
                    endpoint: NOTIFICATION_ENDPOINTS.UPDATE_NOTIFICATION_METHODS,
                    genericErrorMessage: "Could not update notification method",
                    genericSuccessMessage: "Notification method updated",
                }}
                isAutoSave={true}
                noFormTag={true}
                formValues={defaultMethodsPreference}
                onMutationSuccess={() => {
                    void queryClient.invalidateQueries({ queryKey: notificationMethodsQueryKey })
                }}
            >
                {() => (
                    <>
                        {hasVerifiedEmail ? (
                            <ToggleListItem<NotificationMethodsList>
                                isControlled={true}
                                label="Email"
                                caption={user?.email}
                                name="contact_email"
                                isLoading={isLoading}
                            />
                        ) : (
                            <EmailFlowDialog>
                                {({ onClick }: { onClick: () => void }) => (
                                    <ListItem isClickable={true} onClick={onClick}>
                                        <ListItem.AvatarAndContent
                                            avatar={
                                                <Avatar
                                                    variant="object"
                                                    size="xl"
                                                    icon={faIcon({ name: "triangle-exclamation", style: "solid" })}
                                                    baseClassName={styles.warningAvatarBackground}
                                                    iconClassName={styles.warningAvatarIcon}
                                                />
                                            }
                                            content={
                                                <ListItemContent
                                                    leftLabel={<ListItem.Label label="Email" />}
                                                    leftCaption={
                                                        user?.email ? (
                                                            <ListItem.Caption
                                                                caption={`Verify ${user.email}`}
                                                                state="warning"
                                                                truncate={1}
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            }
                                        />
                                        <ListItem.Actions showChevron={true} />
                                    </ListItem>
                                )}
                            </EmailFlowDialog>
                        )}
                        <ListItemDivider />

                        <PhoneFlowDialog>
                            {({ onClick }: { onClick: () => void }) =>
                                hasVerifiedPhone ? (
                                    <ToggleListItem<NotificationMethodsList>
                                        isControlled={true}
                                        label="Text message"
                                        caption={user?.phone ? formatPhoneNumber(user.phone) : undefined}
                                        name="contact_sms"
                                        isLoading={isLoading}
                                    />
                                ) : !hasPhoneNumber ? (
                                    <ListItem>
                                        <ListItem.AvatarAndContent
                                            content={
                                                <ListItemContent leftLabel={<ListItem.Label label="Text message" />} />
                                            }
                                        />
                                        <ListItem.Actions>
                                            <Button onClick={() => {}} size="lg" variant="subtle" isFullWidth={true}>
                                                Set phone number
                                            </Button>
                                        </ListItem.Actions>
                                    </ListItem>
                                ) : (
                                    <ListItem isClickable={true} onClick={onClick}>
                                        <ListItem.AvatarAndContent
                                            avatar={
                                                <Avatar
                                                    variant="object"
                                                    size="xl"
                                                    icon={faIcon({ name: "triangle-exclamation", style: "solid" })}
                                                    baseClassName={styles.warningAvatarBackground}
                                                    iconClassName={styles.warningAvatarIcon}
                                                />
                                            }
                                            content={
                                                <ListItemContent
                                                    leftLabel={<ListItem.Label label="Text message" />}
                                                    leftCaption={
                                                        user?.phone ? (
                                                            <ListItem.Caption
                                                                caption={`Verify ${formatPhoneNumber(user.phone)}`}
                                                                state="warning"
                                                                truncate={1}
                                                            />
                                                        ) : null
                                                    }
                                                />
                                            }
                                        />
                                        <ListItem.Actions showChevron={true} />
                                    </ListItem>
                                )
                            }
                        </PhoneFlowDialog>
                    </>
                )}
            </GenericForm>
        </ListItemStack>
    )
}
